import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, ArrowUpRight, ArrowDownRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const SCREENS = [
  {
    icon: ShieldCheck,
    accent: "text-foreground",
    ring: "border-border bg-secondary",
    title: "Byto. La bóveda para tu dinero.",
    subtitle:
      "Un sistema intuitivo y privado para controlar tu flujo financiero en MXN con visión internacional.",
  },
  {
    icon: ArrowUpRight,
    accent: "text-emerald-500",
    ring: "border-emerald-500/30 bg-emerald-500/10",
    title: "By: Registra el esfuerzo que generó tu valor.",
    subtitle:
      "Flecha hacia arriba en tono esmeralda. Todo ingreso, rendimiento o fondo que entra a tu bóveda tiene su origen en «By».",
  },
  {
    icon: ArrowDownRight,
    accent: "text-rose-500",
    ring: "border-rose-500/30 bg-rose-500/10",
    title: "To: Decide el propósito exacto de cada peso.",
    subtitle:
      "Flecha hacia abajo en rojo tenue. Cada gasto o transferencia tiene un destino claro y medible en «To».",
  },
];

export default function Onboarding({ onFinish }) {
  const [step, setStep] = useState(0);
  const screen = SCREENS[step];
  const Icon = screen.icon;
  const isLast = step === SCREENS.length - 1;

  const next = () => (isLast ? onFinish() : setStep((s) => s + 1));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-md p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-2xl"
        data-testid="onboarding-modal"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col items-start text-left"
          >
            <div className={`w-16 h-16 rounded-2xl border flex items-center justify-center mb-6 ${screen.ring}`}>
              <Icon className={`w-8 h-8 ${screen.accent}`} strokeWidth={2.2} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight leading-snug mb-3">{screen.title}</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">{screen.subtitle}</p>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-8">
          <div className="flex gap-1.5">
            {SCREENS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? "w-6 bg-emerald-500" : "w-1.5 bg-border"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {!isLast && (
              <button
                data-testid="onboarding-skip-button"
                onClick={onFinish}
                className="text-sm text-muted-foreground hover:text-foreground px-2"
              >
                Omitir
              </button>
            )}
            <Button data-testid="onboarding-next-button" onClick={next} className="rounded-xl gap-1">
              {isLast ? "Entrar a Byto" : "Siguiente"}
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
